import { useState } from 'react';
import { ArrowLeft, Activity, Edit, Trash2, Plus, History, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Slider } from './ui/slider';
import { toast } from 'sonner';
import { PatientLayout } from './patient/PatientLayout';
import { LoadingState } from './common/LoadingState';
import { testsApi } from '../lib/api/tests';
import { useAuth } from '../lib/hooks/useAuth';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface SixMinuteWalkTestProps {
  onBack?: () => void;
  onNavigate?: (view: string) => void;
  onLogout?: () => void;
  selectedPatient?: any;
}

export function SixMinuteWalkTestWithCRUD({ onBack, onNavigate, onLogout, selectedPatient }: SixMinuteWalkTestProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const patientId = selectedPatient?.id || user?.patient_id || user?.id || 0;

  // Fetch 6MWT history
  const { data: historyData, isLoading, refetch } = useQuery({
    queryKey: ['6mwt-history', patientId],
    queryFn: () => testsApi.getSixMWTHistory(patientId, { days: 90 }),
    enabled: !!patientId,
  });

  // View mode
  const [showForm, setShowForm] = useState(true);
  const [editingTest, setEditingTest] = useState<any>(null);
  const [deleteTestId, setDeleteTestId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [distanceMeters, setDistanceMeters] = useState('');
  const [preTestSpO2, setPreTestSpO2] = useState('');
  const [preTestHR, setPreTestHR] = useState('');
  const [postTestSpO2, setPostTestSpO2] = useState('');
  const [postTestHR, setPostTestHR] = useState('');
  const [lowestSpO2, setLowestSpO2] = useState('');
  const [borgScore, setBorgScore] = useState<number[]>([0]);
  const [testLocation, setTestLocation] = useState('home');
  const [environmentalConditions, setEnvironmentalConditions] = useState('');
  const [notes, setNotes] = useState('');

  const resetForm = () => {
    setDistanceMeters('');
    setPreTestSpO2('');
    setPreTestHR('');
    setPostTestSpO2('');
    setPostTestHR('');
    setLowestSpO2('');
    setBorgScore([0]);
    setTestLocation('home');
    setEnvironmentalConditions('');
    setNotes('');
    setEditingTest(null);
  };

  const handleEdit = (test: any) => {
    setEditingTest(test);
    setDistanceMeters(test.distance_meters?.toString() || '');
    setPreTestSpO2(test.pre_test_spo2?.toString() || '');
    setPreTestHR(test.pre_test_hr?.toString() || '');
    setPostTestSpO2(test.post_test_spo2?.toString() || '');
    setPostTestHR(test.post_test_hr?.toString() || '');
    setLowestSpO2(test.lowest_spo2?.toString() || '');
    setBorgScore([test.borg_score || 0]);
    setTestLocation(test.test_location || 'home');
    setEnvironmentalConditions(test.environmental_conditions || '');
    setNotes(test.notes || '');
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!distanceMeters) {
      toast.error('Required field missing', {
        description: 'Please enter distance walked',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const data = {
        distance_meters: parseFloat(distanceMeters),
        pre_spo2: preTestSpO2 ? parseInt(preTestSpO2) : undefined,  // ✅ Fixed: was pre_test_spo2
        pre_heart_rate: preTestHR ? parseInt(preTestHR) : undefined,  // ✅ Fixed: was pre_test_hr
        post_spo2: postTestSpO2 ? parseInt(postTestSpO2) : undefined,  // ✅ Fixed: was post_test_spo2
        post_heart_rate: postTestHR ? parseInt(postTestHR) : undefined,  // ✅ Fixed: was post_test_hr
        lowest_spo2: lowestSpO2 ? parseInt(lowestSpO2) : undefined,
        dyspnea_score: borgScore[0],  // ✅ Fixed: was borg_score
        test_location: testLocation,
        environmental_conditions: environmentalConditions || undefined,
        notes: notes || undefined,
      };

      if (editingTest) {
        // Update existing
        await testsApi.updateSixMWT(editingTest.id, data);
        toast.success('6-Minute Walk Test updated successfully!');
      } else {
        // Create new - ✅ Fixed: Send patient_id directly
        await testsApi.recordSixMWT({
          patient_id: patientId,
          ...data,
        });
        toast.success('6-Minute Walk Test recorded successfully!');
      }

      resetForm();
      refetch();
      queryClient.invalidateQueries({ queryKey: ['patient-dashboard'] });
    } catch (error: any) {
      toast.error('Failed to save test', {
        description: error?.message || 'An error occurred',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTestId) return;

    try {
      await testsApi.deleteSixMWT(deleteTestId);
      toast.success('6-Minute Walk Test deleted successfully!');
      setDeleteTestId(null);
      refetch();
      queryClient.invalidateQueries({ queryKey: ['patient-dashboard'] });
    } catch (error: any) {
      toast.error('Failed to delete test', {
        description: error?.message || 'An error occurred',
      });
    }
  };

  const getDistanceStatus = (distance: number) => {
    // Reference values (simplified)
    if (distance >= 400) return { label: 'Good', color: 'bg-[#14CAF4]' };
    if (distance >= 250) return { label: 'Moderate', color: 'bg-accent' };
    return { label: 'Low', color: 'bg-destructive' };
  };

  const getSpO2Status = (spo2: number) => {
    if (spo2 >= 95) return { label: 'Normal', color: 'text-[#14CAF4]' };
    if (spo2 >= 90) return { label: 'Low', color: 'text-accent' };
    return { label: 'Critical', color: 'text-destructive' };
  };

  return (
    <PatientLayout currentView="tests" onNavigate={onNavigate} onLogout={onLogout}>
      <div className="p-4 space-y-4">
        {/* Header with toggle */}
        <div className="flex items-center justify-between">
          {onBack && (
            <Button variant="outline" onClick={onBack} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2"
          >
            {showForm ? <History className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? 'View History' : 'Record New'}
          </Button>
        </div>

        {showForm ? (
          /* Input Form */
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-primary flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                {editingTest ? 'Edit 6-Minute Walk Test' : 'Record 6-Minute Walk Test'}
              </CardTitle>
              <p className="text-muted-foreground">Enter test results</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Distance */}
              <div className="space-y-2">
                <Label htmlFor="distance">Distance Walked (meters) *</Label>
                <Input
                  id="distance"
                  type="number"
                  step="1"
                  placeholder="e.g., 350"
                  value={distanceMeters}
                  onChange={(e) => setDistanceMeters(e.target.value)}
                />
                {distanceMeters && (
                  <Badge className={getDistanceStatus(parseFloat(distanceMeters)).color}>
                    {getDistanceStatus(parseFloat(distanceMeters)).label}
                  </Badge>
                )}
              </div>

              {/* Pre-Test Vitals */}
              <div>
                <h3 className="font-medium mb-3">Pre-Test Vitals</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pre-spo2">SpO₂ (%)</Label>
                    <Input
                      id="pre-spo2"
                      type="number"
                      step="0.1"
                      placeholder="e.g., 96"
                      value={preTestSpO2}
                      onChange={(e) => setPreTestSpO2(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pre-hr">Heart Rate (bpm)</Label>
                    <Input
                      id="pre-hr"
                      type="number"
                      step="1"
                      placeholder="e.g., 75"
                      value={preTestHR}
                      onChange={(e) => setPreTestHR(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Post-Test Vitals */}
              <div>
                <h3 className="font-medium mb-3">Post-Test Vitals</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="post-spo2">SpO₂ (%)</Label>
                    <Input
                      id="post-spo2"
                      type="number"
                      step="0.1"
                      placeholder="e.g., 92"
                      value={postTestSpO2}
                      onChange={(e) => setPostTestSpO2(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="post-hr">Heart Rate (bpm)</Label>
                    <Input
                      id="post-hr"
                      type="number"
                      step="1"
                      placeholder="e.g., 110"
                      value={postTestHR}
                      onChange={(e) => setPostTestHR(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Lowest SpO2 */}
              <div className="space-y-2">
                <Label htmlFor="lowest-spo2">Lowest SpO₂ During Test (%)</Label>
                <Input
                  id="lowest-spo2"
                  type="number"
                  step="0.1"
                  placeholder="e.g., 90"
                  value={lowestSpO2}
                  onChange={(e) => setLowestSpO2(e.target.value)}
                />
                {lowestSpO2 && (
                  <p className={`text-sm ${getSpO2Status(parseFloat(lowestSpO2)).color}`}>
                    {getSpO2Status(parseFloat(lowestSpO2)).label}
                  </p>
                )}
              </div>

              {/* Borg Score */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="borg">Borg Dyspnea Score</Label>
                  <Badge variant="outline">{borgScore[0]}/10</Badge>
                </div>
                <Slider
                  id="borg"
                  value={borgScore}
                  onValueChange={setBorgScore}
                  max={10}
                  step={1}
                  className="w-full"
                />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>No breathlessness</span>
                  <span>Maximum breathlessness</span>
                </div>
              </div>

              {/* Test Location */}
              <div className="space-y-2">
                <Label htmlFor="location">Test Location</Label>
                <Select value={testLocation} onValueChange={setTestLocation}>
                  <SelectTrigger id="location">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home">Home</SelectItem>
                    <SelectItem value="clinic">Clinic</SelectItem>
                    <SelectItem value="hospital">Hospital</SelectItem>
                    <SelectItem value="park">Park/Outdoor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Environmental Conditions */}
              <div className="space-y-2">
                <Label htmlFor="conditions">Environmental Conditions (Optional)</Label>
                <Input
                  id="conditions"
                  placeholder="e.g., Indoor, 22°C, flat surface"
                  value={environmentalConditions}
                  onChange={(e) => setEnvironmentalConditions(e.target.value)}
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional notes about the test..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {editingTest && (
                  <Button variant="outline" onClick={resetForm} className="flex-1">
                    Cancel
                  </Button>
                )}
                <Button 
                  onClick={handleSubmit} 
                  disabled={isSubmitting}
                  className="flex-1 bg-primary"
                >
                  {isSubmitting ? 'Saving...' : editingTest ? 'Update Test' : 'Save Test'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* History View */
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-primary flex items-center gap-2">
                <History className="w-5 h-5" />
                6-Minute Walk Test History
              </CardTitle>
              <p className="text-muted-foreground">Past 90 days</p>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <LoadingState message="Loading test history..." />
              ) : !historyData?.results || historyData.results.length === 0 ? (
                <div className="text-center py-8">
                  <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No tests recorded yet</p>
                  <Button onClick={() => setShowForm(true)} className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Record First Test
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {historyData.results.map((test: any) => (
                    <div
                      key={test.id}
                      className="p-4 border-2 border-border rounded-xl hover:bg-secondary/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-medium">{new Date(test.test_date).toLocaleDateString()}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={getDistanceStatus(test.distance_meters || 0).color}>
                              {test.distance_meters}m
                            </Badge>
                            <Badge className={getDistanceStatus(test.distance_meters || 0).color}>
                              {getDistanceStatus(test.distance_meters || 0).label}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleEdit(test)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteTestId(test.id)}
                            className="text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {test.pre_test_spo2 && (
                          <div>
                            <span className="text-muted-foreground">Pre SpO₂:</span>
                            <span className="ml-2 font-medium">{test.pre_test_spo2}%</span>
                          </div>
                        )}
                        {test.post_test_spo2 && (
                          <div>
                            <span className="text-muted-foreground">Post SpO₂:</span>
                            <span className="ml-2 font-medium">{test.post_test_spo2}%</span>
                          </div>
                        )}
                        {test.pre_test_hr && (
                          <div>
                            <span className="text-muted-foreground">Pre HR:</span>
                            <span className="ml-2 font-medium">{test.pre_test_hr} bpm</span>
                          </div>
                        )}
                        {test.post_test_hr && (
                          <div>
                            <span className="text-muted-foreground">Post HR:</span>
                            <span className="ml-2 font-medium">{test.post_test_hr} bpm</span>
                          </div>
                        )}
                        {test.lowest_spo2 && (
                          <div>
                            <span className="text-muted-foreground">Lowest SpO₂:</span>
                            <span className="ml-2 font-medium">{test.lowest_spo2}%</span>
                          </div>
                        )}
                        {test.borg_score !== null && test.borg_score !== undefined && (
                          <div>
                            <span className="text-muted-foreground">Borg Score:</span>
                            <span className="ml-2 font-medium">{test.borg_score}/10</span>
                          </div>
                        )}
                        {test.test_location && (
                          <div>
                            <span className="text-muted-foreground">Location:</span>
                            <span className="ml-2 font-medium">{test.test_location}</span>
                          </div>
                        )}
                      </div>
                      {test.environmental_conditions && (
                        <p className="text-sm text-muted-foreground mt-2">
                          Conditions: {test.environmental_conditions}
                        </p>
                      )}
                      {test.notes && (
                        <p className="text-sm text-muted-foreground mt-2 italic">
                          Note: {test.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteTestId} onOpenChange={() => setDeleteTestId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete 6-Minute Walk Test?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete this test result from your history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </PatientLayout>
  );
}